import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../store/store";

import {
  fetchStates,
  fetchLgasByState,
} from "../store/slice/location/locationThunks";

import { fetchServices } from "../store/slice/service/serviceThunks";
import {
  searchProviders,
  searchProvidersNearby,
} from "../store/slice/provider/providerThunks";

import { locationActions } from "../store/slice/location/locationSlice";

const LocationSearch: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();

  const { states, lgas, loading, error, selectedState, selectedLga } =
    useSelector((state: RootState) => state.location);

  const { services } = useSelector((state: RootState) => state.service);
  const [selectedService, setSelectedService] = useState<number | "">("");
  const [nearbyStatus, setNearbyStatus] = useState<
    "idle" | "loading" | "error"
  >("idle");
  const [nearbyError, setNearbyError] = useState("");

  // 🔹 Fetch states & services on mount
  useEffect(() => {
    dispatch(fetchStates());
    dispatch(fetchServices());
  }, [dispatch]);

  const handleStateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const stateId = Number(e.target.value);
    const state = states.find((s) => s.id === stateId) || null;

    dispatch(locationActions.setSelectedState(state));
    dispatch(locationActions.setSelectedLga(null));

    if (state) {
      dispatch(fetchLgasByState(state.id));
    }
  };

  const handleLgaChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const lgaId = Number(e.target.value);
    const lga = lgas.find((l) => l.id === lgaId) || null;
    dispatch(locationActions.setSelectedLga(lga));
  };

  const handleSearch = () => {
    if (!selectedState || !selectedLga || !selectedService) {
      alert("Please select a State, LGA, and Service before searching.");
      return;
    }

    dispatch(
      searchProviders(selectedState.id, selectedLga.id, selectedService)
    );
  };

  const handleSearchNearby = () => {
    if (!selectedService) {
      alert("Please select a Service before searching near you.");
      return;
    }
    const skillName = services.find((s) => s.id === selectedService)?.name;
    if (!skillName) return;

    if (!navigator.geolocation) {
      setNearbyStatus("error");
      setNearbyError("Geolocation isn't supported by this browser");
      return;
    }

    setNearbyStatus("loading");
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setNearbyStatus("idle");
        dispatch(
          searchProvidersNearby(
            position.coords.latitude,
            position.coords.longitude,
            skillName
          )
        );
      },
      (err) => {
        setNearbyStatus("error");
        setNearbyError(err.message || "Couldn't get your location");
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  return (
    <section
      className="bg-center bg-no-repeat bg-green-700 bg-blend-multiply"
      style={{
        backgroundImage:
          'url("https://cdn.pixabay.com/photo/2017/09/09/22/05/site-2733678_1280.jpg")',
      }}
    >
      <div className="px-4 mx-auto max-w-screen-xl text-center py-24 lg:py-56">
        <h1 className="mb-4 text-4xl font-extrabold text-white">
          We unlock the power of local skills and services
        </h1>

        <p className="mb-8 text-lg text-gray-300">
          Find trusted professionals near you.
        </p>

        <div className="bg-white p-6 rounded-lg shadow-lg max-w-sm mx-auto">
          <h2 className="text-2xl font-bold mb-6 text-gray-800">
            Find a Handyman Near You
          </h2>

          <select
            className="w-full mb-4 p-2 border rounded"
            value={selectedState?.id || ""}
            onChange={handleStateChange}
          >
            <option value="">Select a State</option>
            {states.map((state) => (
              <option key={state.id} value={state.id}>
                {state.name}
              </option>
            ))}
          </select>

          <select
            className="w-full mb-4 p-2 border rounded"
            value={selectedLga?.id || ""}
            onChange={handleLgaChange}
            disabled={!selectedState}
          >
            <option value="">Select an LGA</option>
            {lgas.map((lga) => (
              <option key={lga.id} value={lga.id}>
                {lga.name}
              </option>
            ))}
          </select>

          <select
            className="w-full mb-6 p-2 border rounded"
            value={selectedService}
            onChange={(e) => setSelectedService(Number(e.target.value))}
            disabled={!selectedLga}
          >
            <option value="">Select a Service</option>
            {services.map((srv) => (
              <option key={srv.id} value={srv.id}>
                {srv.name}
              </option>
            ))}
          </select>

          <button
            onClick={handleSearch}
            disabled={loading}
            className="w-full bg-yellow-600 hover:bg-yellow-700 text-white font-bold py-2 px-4 rounded"
          >
            Search
          </button>

          <button
            onClick={handleSearchNearby}
            disabled={loading || nearbyStatus === "loading"}
            className="w-full mt-3 border border-yellow-600 text-yellow-700 font-bold py-2 px-4 rounded hover:bg-yellow-50 disabled:opacity-50"
          >
            {nearbyStatus === "loading"
              ? "Finding providers near you..."
              : "Search Near Me"}
          </button>
          {nearbyStatus === "error" && (
            <p className="text-red-500 text-sm mt-1">{nearbyError}</p>
          )}

          {error && <p className="text-red-500 mt-2">{error}</p>}
        </div>
      </div>
    </section>
  );
};

export default LocationSearch;
